/**
 * Failed-invocation rollup for a published arche.
 *
 * DELIBERATELY AGGREGATE. On a published arche the invoking users are the
 * plugin's END USERS, not the contributor reading this page. Per-invocation
 * detail here would be a feed of other people's activity — when they use the
 * plugin, how often, what fails — so the server returns counts grouped by
 * entity and error class over the WHOLE window, with no time dimension at all,
 * and there is no drill-down to add later without re-opening that decision.
 * (A contributor debugging their OWN calls gets full detail on the builder's
 * Failures tab, where every row is theirs.)
 *
 * The hour bucket the server used to send was removed: on a low-traffic plugin
 * a `{hour, count: 1}` row pinpoints when a single end user failed, which is
 * the re-identification the bucketing was meant to prevent. The payload is
 * labelled `window-total`, NOT "de-identified" — a caller who polls this can
 * still watch a count change, and the label should not claim otherwise.
 */

import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface InvocationFailuresSectionProps {
  archeId: string;
}

/** Plain-language names for the gateway's operator-side classifications. */
const ERROR_LABELS: Record<string, string> = {
  interrupted: 'Interrupted',
  timeout: 'Timed out',
  provider_server: 'Provider overloaded',
  rate_limit: 'Rate limited',
  quota: 'Provider quota exhausted',
  auth: 'Provider auth failure',
  bad_request: 'Rejected request',
  validation: 'Invalid request',
  provider: 'Provider error',
  internal: 'Platform error',
  unknown: 'Unclassified',
};

export function InvocationFailuresSection({ archeId }: InvocationFailuresSectionProps) {
  const { data, isPending, isError } = useQuery({
    queryKey: queryKeys.contributorArches.invocationFailures(archeId),
    queryFn: () => api.getArcheInvocationFailures(archeId),
  });

  // Consumed as sent: the server groups by (entity, error class) and orders by
  // count descending, so there is nothing left to reshape client-side.
  const buckets = data?.buckets ?? [];
  const total = buckets.reduce((sum, bucket) => sum + bucket.failureCount, 0);

  return (
    <section className="mt-8">
      <h2 className="text-lg font-semibold">Failed invocations</h2>
      <p className="mb-3 text-sm text-muted-foreground">
        AI text generation calls from this arche that failed in the last 7 days, grouped by reason.
        Counts only — individual invocations belong to the users who made them. Integration calls
        (social posting, storage, data) are not covered yet.
      </p>

      {isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : isError ? (
        <p className="text-sm text-muted-foreground">Could not load invocation failures.</p>
      ) : buckets.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No failed invocations recorded in the last 7 days.
        </p>
      ) : (
        <>
          <div className="mb-2 flex items-center gap-2 text-sm">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <span>
              {total} failed {total === 1 ? 'call' : 'calls'}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Reason</th>
                  <th className="py-2 pr-4 font-medium">Entity</th>
                  <th className="py-2 font-medium text-right">Count</th>
                </tr>
              </thead>
              <tbody>
                {buckets.map((bucket) => {
                  const errorCode = bucket.errorCode ?? 'unknown';
                  return (
                    <tr
                      key={`${bucket.entityId}::${errorCode}`}
                      className="border-b last:border-b-0"
                    >
                      <td className="py-2 pr-4">{ERROR_LABELS[errorCode] ?? errorCode}</td>
                      <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">
                        {bucket.entityId}
                      </td>
                      <td className="py-2 text-right tabular-nums">{bucket.failureCount}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
